// src/features/videos/RankedVideoCard.tsx — a numbered "Trending now" card.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatViews } from './meta';
import type { VideoItem, VideoCreator } from '@/core/models/video';
import { VideoThumb } from './VideoThumb';

const Card = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  text-decoration: none;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  transition:
    transform 120ms ease,
    border-color 120ms ease;
`;

const Rank = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const ThumbWrap = styled.div`
  position: relative;
  height: 100px;
`;

const Body = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface RankedVideoCardProps {
  video: VideoItem;
  rank: number;
  creator?: VideoCreator;
}

export function RankedVideoCard({ video, rank, creator }: RankedVideoCardProps) {
  return (
    <Card to={`/videos/${video.id}`} aria-label={`#${rank} ${video.title}`}>
      <Rank aria-hidden="true">{rank}</Rank>
      <ThumbWrap>
        <VideoThumb video={video} />
      </ThumbWrap>
      <Body>
        <Title>{video.title}</Title>
        {creator && <Meta>{creator.name}</Meta>}
        <Meta>{formatViews(video.views)}</Meta>
      </Body>
    </Card>
  );
}
